/**
 * Beat ↔ seconds conversions and snap-grid helpers.
 *
 * Most rhythm games share the same underlying math:
 *   beat   = seconds * (bpm / 60)
 *   seconds = beat * (60 / bpm)
 *
 * Some games (Synth Rider) distinguish "raw beat" (no offset) from
 * "display beat" (offset-subtracted). The difficulty engine and the
 * note timestamps always use RAW beat; the UI may apply offset on top.
 */

import type { BeatmapTiming } from './types';

/** Convert song time (seconds) to raw beat number. */
export function secondsToBeat(seconds: number, bpm: number): number {
  if (bpm <= 0) return 0;
  return Math.max(0, (seconds / 60) * bpm);
}

/** Convert raw beat number back to song time (seconds). */
export function beatToSeconds(beat: number, bpm: number): number {
  if (bpm <= 0) return 0;
  return Math.max(0, (beat / bpm) * 60);
}

/**
 * Snap a beat value to the nearest grid step.
 * `snapDivision` is "subdivisions per quarter note" — 4 = 1/16 grid, 8 = 1/32, etc.
 */
export function snapBeat(beat: number, snapDivision: number): number {
  if (snapDivision <= 0) return beat;
  const step = 1 / (snapDivision / 4);
  return Math.max(0, Math.round(beat / step) * step);
}

/** Apply offset to convert raw beat → display beat (what the UI shows). */
export function applyOffset(rawBeat: number, timing: BeatmapTiming): number {
  if (!timing.offset_seconds) return rawBeat;
  const offsetBeats = secondsToBeat(timing.offset_seconds, timing.bpm);
  return rawBeat - offsetBeats;
}

/** Reverse: display beat (what the user picked) → raw beat (the storage form). */
export function removeOffset(displayBeat: number, timing: BeatmapTiming): number {
  if (!timing.offset_seconds) return displayBeat;
  const offsetBeats = secondsToBeat(timing.offset_seconds, timing.bpm);
  return displayBeat + offsetBeats;
}

/** How long one beat lasts at this tempo, in seconds. */
export function secondsPerBeat(bpm: number): number {
  return bpm > 0 ? 60 / bpm : 0;
}
